import {
	Link,
	NavLink,
	useLocation,
	useRouteLoaderData,
} from '@remix-run/react'
import { motion } from 'framer-motion'
import { type loader as rootLoader } from '#app/root.tsx'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { useMediaQuery } from '#app/utils/media-query.ts'
import { cn } from '#app/utils/misc.tsx'
import { type Theme } from '#app/utils/theme.server.ts'
import { Icon } from '../../ui/icon.tsx'
import { MobileMenu } from './mobile-menu'
import { paths } from './paths'

const MotionNavLink = motion(NavLink)

export default function NavigationBar({ theme }: { theme?: Theme }) {
	const mdDown = useMediaQuery('md-down')
	const smDown = useMediaQuery('sm-down')
	const isMobileOrTablet = mdDown || smDown
	const location = useLocation()
	const rootData = useRouteLoaderData<typeof rootLoader>('root')
	const isAdmin = rootData?.user?.roles.some((role) => role.name === 'admin')
	const guestUser = rootData?.guestUser

	const handleNavClick = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const handleNavKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		}
	}

	const getActiveClassName = (path: { to: string; name: string }) => {
		let isActive = false

		// Handle exact matches first
		if (location.pathname === path.to) {
			isActive = true
		}
		// Handle work routes - any /work path should be active for /work
		else if (path.to === '/work' && location.pathname.startsWith('/work')) {
			isActive = true
		}
		// Handle education routes - any /education path should be active for /education
		else if (
			path.to === '/education' &&
			location.pathname.startsWith('/education')
		) {
			isActive = true
		}
		// Handle home/about routes - both / and anything starting with /about should activate "About Me"
		else if (
			path.to === '/' &&
			(location.pathname.startsWith('/about') || location.pathname === '/')
		) {
			isActive = true
		}

		return cn(
			'activeLink whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200',
			isActive
				? 'bg-primary font-semibold text-primary-foreground shadow-md'
				: 'hover:bg-accent hover:text-accent-foreground',
		)
	}

	const getAdminActiveClassName = () => {
		const isAdminActive = location.pathname.startsWith('/admin')
		return cn(
			'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
			isAdminActive
				? 'bg-primary font-semibold text-primary-foreground shadow-md'
				: 'hover:bg-accent hover:text-accent-foreground',
		)
	}

	return (
		<>
			{isMobileOrTablet ? (
				<div className="flex w-full items-center justify-between px-4">
					<MobileMenu isAdmin={isAdmin} />
					<span className="flex flex-1 justify-center">
						<NavLink
							className="flex items-center justify-center whitespace-nowrap text-lg font-semibold text-foreground"
							to="/"
							aria-label="Go to homepage"
							onClick={handleNavClick}
							onKeyDown={handleNavKeyDown}
						>
							Nicolás F. Mainella
						</NavLink>
					</span>
					<ThemeSwitch userPreference={theme} />
				</div>
			) : (
				<div className="mx-auto flex w-full min-w-0 max-w-7xl items-center justify-between px-4">
					<NavLink
						className="flex items-center justify-center whitespace-nowrap text-lg font-semibold text-foreground"
						to="/"
						aria-label="Go to homepage"
						onClick={handleNavClick}
						onKeyDown={handleNavKeyDown}
					>
						Nicolás F. Mainella
					</NavLink>
					<nav
						className="mx-4 flex flex-shrink-0 justify-center"
						role="navigation"
						aria-label="Main navigation"
					>
						<ul className="flex flex-row gap-1" role="list">
							{paths.map((path) => (
								<li key={path.name} role="listitem">
									<MotionNavLink
										to={path.to}
										className={getActiveClassName(path)}
										aria-label={`Go to ${path.name}`}
										onClick={handleNavClick}
										whileHover={{ scale: 1.02, y: -1 }}
										whileTap={{ scale: 0.98 }}
										transition={{ duration: 0.2 }}
									>
										{path.name}
									</MotionNavLink>
								</li>
							))}
						</ul>
					</nav>
					<div className="flex items-center gap-2">
						{/* Show admin status if user is admin, otherwise show guest status */}
						{isAdmin ? (
							<Link
								to="/admin"
								aria-label="Admin Dashboard"
								className={cn(getAdminActiveClassName(), 'hidden md:flex')}
								onClick={handleNavClick}
								onKeyDown={handleNavKeyDown}
							>
								<Icon name="lock-closed" />
								<span>Admin</span>
							</Link>
						) : (
							guestUser && (
								<div className="flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm">
									<Icon name="avatar" className="h-4 w-4" />
									<span className="font-medium">{guestUser.name}</span>
									<span className="text-xs text-muted-foreground">(Guest)</span>
								</div>
							)
						)}
						<ThemeSwitch userPreference={theme} />
					</div>
				</div>
			)}
		</>
	)
}
